import { useCallback, useMemo, useState } from 'react'
import type { PostCard } from '../types'
import { useGiftCatalog, type GiftDef } from './useGifts'
import { sendGift } from '../api/gifts'
import { newIdempotencyKey } from '../lib/idempotency'

export type UseGiftFlowArgs = {
  updateTips?: (postId: number | string, tips: number) => void
}

export function useGiftFlow({ updateTips }: UseGiftFlowArgs = {}) {
  const { items: catalogDefs } = useGiftCatalog()

  const [isOpen, setIsOpen] = useState(false)
  const [post, setPost] = useState<PostCard | null>(null)

  const [toastVisible, setToastVisible] = useState(false)
  const [toastEmoji, setToastEmoji] = useState('🎁')
  const [toastLabel, setToastLabel] = useState('Gift sent!')
  const [toastDetail, setToastDetail] = useState<string | undefined>(undefined)
  const [toastKind, setToastKind] = useState<'success' | 'error'>('success')
  const [celebrate, setCelebrate] = useState(false)

  const openForPost = useCallback((p: PostCard) => {
    setPost(p)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => setIsOpen(false), [])
  const hideToast = useCallback(() => setToastVisible(false), [])

  const onSend = useCallback(async (args: { gift: GiftDef; note?: string; anon?: boolean; customAmountCents?: number }) => {
    if (!post) return
    try {
      const res = await sendGift(post.id, {
        gift_id: args.gift.id,
        custom_amount_cents: args.customAmountCents,
        note: args.note,
        anon: args.anon,
        idempotency_key: newIdempotencyKey('gift'),
      })
      if (typeof res?.totals?.tips === 'number' && updateTips) {
        updateTips(post.id, res.totals.tips)
      }
      const receipt = (res as any)?.ledger_entry_id ? `Receipt #${(res as any).ledger_entry_id}` : undefined
      setToastEmoji(args.gift.emoji)
      setToastLabel(`Sent ${args.gift.name}`)
      setToastKind('success')
      setCelebrate(true)
      const to = post?.user?.displayName ? `to ${post.user.displayName}` : undefined
      setToastDetail(receipt ? (to ? `${to} - ${receipt}` : receipt) : to)
      setToastVisible(true)
    } catch (e: any) {
      setToastEmoji('⚠️')
      setToastLabel('Gift failed')
      setToastKind('error')
      setCelebrate(false)
      setToastDetail(e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Please try again')
      setToastVisible(true)
    } finally {
      setIsOpen(false)
    }
  }, [post, updateTips])

  return {
    isOpen,
    openForPost,
    close,
    post,
    catalogDefs,
    onSend,
    toast: {
      visible: toastVisible,
      emoji: toastEmoji,
      label: toastLabel,
      detail: toastDetail,
      kind: toastKind,
      celebrate,
      hide: hideToast,
    },
  }
}
